(() => {
  if (customElements.get("circadian-entrance")) return;

  class CircadianEntrance extends HTMLElement {
    constructor() {
      super();
      this.intervalId = null;
      this.timeoutId = null;
      this.dayStart = 6;
      this.dayEnd = 18;
      this.timeLabel = null;
      this.hourEl = null;
      this.minuteEl = null;
      this.titleEl = null;
      this.actionEl = null;
      this.actionTextEl = null;
      this.dayTitle = "";
      this.nightTitle = "";
      this.dayActionText = "";
      this.nightActionText = "";
      this.dayActionLink = "";
      this.nightActionLink = "";
      this.dayIcon = null;
      this.nightIcon = null;
      this.initRafId = null;
      this.lastIsDay = null;
      this.lastHour = "";
      this.lastMinute = "";
      this.lastTimeText = "";
    }

    connectedCallback() {
      this.dayStart = this.getNumberAttr("dayStart", 6);
      this.dayEnd = this.getNumberAttr("dayEnd", 18);
      this.timeLabel = this.querySelector("[data-circadian-time]");
      this.hourEl = this.querySelector("[data-circadian-hour]");
      this.minuteEl = this.querySelector("[data-circadian-minute]");
      this.titleEl = this.querySelector("[data-circadian-heading]");
      this.actionEl = this.querySelector("[data-circadian-action]");
      this.actionTextEl = this.querySelector("[data-circadian-action-text]");
      this.dayIcon = this.querySelector("[data-circadian-icon='day']");
      this.nightIcon = this.querySelector("[data-circadian-icon='night']");

      if (this.titleEl) {
        this.dayTitle = this.titleEl.dataset.dayText || "";
        this.nightTitle = this.titleEl.dataset.nightText || "";
      }

      if (this.actionEl) {
        this.dayActionText = this.actionEl.dataset.dayText || "";
        this.nightActionText = this.actionEl.dataset.nightText || "";
        this.dayActionLink = this.actionEl.dataset.dayLink || "";
        this.nightActionLink = this.actionEl.dataset.nightLink || "";
      }

      this.dataset.initialized = "false";
      this.updateTime();
      this.initRafId = window.requestAnimationFrame(() => {
        this.dataset.initialized = "true";
        this.initRafId = null;
      });
      this.startTimer();
    }

    disconnectedCallback() {
      if (this.initRafId) {
        window.cancelAnimationFrame(this.initRafId);
        this.initRafId = null;
      }
      this.clearTimer();
    }

    getNumberAttr(name, fallback) {
      const value = Number(this.dataset[name]);
      return Number.isFinite(value) ? value : fallback;
    }

    getTimeParts(now) {
      const fallback = {
        hour: String(now.getHours()).padStart(2, "0"),
        minute: String(now.getMinutes()).padStart(2, "0"),
        separator: ":",
      };

      if (typeof Intl === "undefined" || !Intl.DateTimeFormat) {
        return fallback;
      }

      try {
        const formatter = new Intl.DateTimeFormat(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          numberingSystem: "latn",
        });
        if (typeof formatter.formatToParts !== "function") {
          return fallback;
        }
        const parts = formatter.formatToParts(now);
        const hour = parts.find((part) => part.type === "hour")?.value;
        const minute = parts.find((part) => part.type === "minute")?.value;
        const separator = parts.find((part) => part.type === "literal")?.value;
        return {
          hour: hour || fallback.hour,
          minute: minute || fallback.minute,
          separator: separator || fallback.separator,
        };
      } catch (error) {
        return fallback;
      }
    }

    clearTimer() {
      if (this.timeoutId) {
        window.clearTimeout(this.timeoutId);
        this.timeoutId = null;
      }
      if (this.intervalId) {
        window.clearInterval(this.intervalId);
        this.intervalId = null;
      }
    }

    startTimer() {
      this.clearTimer();
      const now = new Date();
      const msToNextMinute =
        (60 - now.getSeconds()) * 1000 - now.getMilliseconds();
      this.timeoutId = window.setTimeout(
        () => {
          this.updateTime();
          this.intervalId = window.setInterval(() => this.updateTime(), 60000);
        },
        Math.max(msToNextMinute, 0),
      );
    }

    isDaytime(hour) {
      if (this.dayStart <= this.dayEnd) {
        return hour >= this.dayStart && hour < this.dayEnd;
      }

      return hour >= this.dayStart || hour < this.dayEnd;
    }

    updateTime() {
      const now = new Date();
      const hour = now.getHours();
      const isDay = this.isDaytime(hour);
      const timeParts = this.getTimeParts(now);
      const isModeChanged = this.lastIsDay === null || this.lastIsDay !== isDay;

      if (isModeChanged) {
        this.dataset.mode = isDay ? "day" : "night";
      }

      if (this.hourEl && this.minuteEl) {
        if (timeParts.hour !== this.lastHour) {
          this.hourEl.textContent = timeParts.hour;
        }
        if (timeParts.minute !== this.lastMinute) {
          this.minuteEl.textContent = timeParts.minute;
        }
      } else if (this.timeLabel) {
        const nextText = `${timeParts.hour}${timeParts.separator}${timeParts.minute}`;
        if (nextText !== this.lastTimeText) {
          this.timeLabel.textContent = nextText;
          this.lastTimeText = nextText;
        }
      }

      if (isModeChanged) {
        if (this.titleEl) {
          this.titleEl.textContent = isDay ? this.dayTitle : this.nightTitle;
        }

        if (this.actionTextEl) {
          this.actionTextEl.textContent = isDay
            ? this.dayActionText
            : this.nightActionText;
        }

        if (this.actionEl) {
          const link = isDay ? this.dayActionLink : this.nightActionLink;
          const hasLink = Boolean(link);

          if (hasLink) {
            this.actionEl.setAttribute("href", link);
            this.actionEl.removeAttribute("aria-disabled");
            this.actionEl.removeAttribute("role");
            this.actionEl.removeAttribute("tabindex");
          } else {
            this.actionEl.removeAttribute("href");
            this.actionEl.setAttribute("role", "link");
            this.actionEl.setAttribute("aria-disabled", "true");
            this.actionEl.setAttribute("tabindex", "-1");
          }
        }

        if (this.dayIcon && this.nightIcon) {
          this.dayIcon.classList.toggle("hidden", !isDay);
          this.nightIcon.classList.toggle("hidden", isDay);
        }
      }

      this.lastIsDay = isDay;
      this.lastHour = timeParts.hour;
      this.lastMinute = timeParts.minute;
    }
  }

  customElements.define("circadian-entrance", CircadianEntrance);
})();
